import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRequireAuth } from '../hooks/use-auth-hooks';
import { useProfile } from '../hooks/useProfile';
import { useApiService } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { RelationshipModal } from "@/components/relationship-modal";
import { Search, Filter, MoreHorizontal, ChevronLeft, ChevronRight, RotateCcw, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { formatSubjectName } from '@/utils/format';

// Types for our data - updated to match backend API
interface Class {
  id: string;
  name: string;
  description: string;
  grade: string;
  metadata?: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

interface Subject {
  id: string;
  display_name: string;
  name: string;
  description: string;
  classId: string;
  createdAt: string;
  updatedAt: string;
}

interface Chapter {
  id: string;
  title: string;
  order: number;
  subject_id: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

interface Beacon {
  id: string;
  type: string;
  name: string;
  subject_id: string;
  chapter_id: string;
  definition?: string;
  description?: string;
  grade?: string;
  examples?: string[];
  keywords?: string[];
  date?: string;
  location?: string;
  key_outcomes?: string[];
  birth_date?: string;
  death_date?: string;
  role?: string;
  contributions?: string[];
  linked_concept?: string;
  source?: {
    textbook: string;
    page?: string;
  };
  relationships?: Array<{
    type: string;
    target_id: string;
    target_name: string;
    target_type: string;
    strength?: number;
    description?: string;
  }>;
}

export function BeaconsBrowserPage() {
  const { user } = useRequireAuth();
  const { profile, isLoading: profileLoading } = useProfile();
  const navigate = useNavigate();
  const apiService = useApiService();
  
  // Determine user type from profile
  const userType = profile?.profile_type || 'student';
  const isStudent = userType === 'student';
  
  // State for form controls
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [selectedChapter, setSelectedChapter] = useState<string>('');
  
  // State for data
  const [classes, setClasses] = useState<Class[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [allBeacons, setAllBeacons] = useState<Beacon[]>([]); // All beacons from API
  const [beacons, setBeacons] = useState<Beacon[]>([]); // Paginated beacons for display
  
  // State for table features
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalBeacons, setTotalBeacons] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [loadingChapters, setLoadingChapters] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = 'Explore Knowledge Beacons - AceHorizon';
  }, []);
  const [error, setError] = useState<string | null>(null);
  
  // Sorting state - default to sorting by relationship count in descending order
  const [sortColumn, setSortColumn] = useState<'name' | 'type' | 'description' | 'relationships'>('relationships');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Relationship modal state
  const [relationshipModalOpen, setRelationshipModalOpen] = useState(false);
  const [selectedBeaconForRelationships, setSelectedBeaconForRelationships] = useState<Beacon | null>(null);
  
  // Dropdown state management to prevent focus conflicts
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  
  // Focus management ref for modal
  const modalTriggerRef = useRef<HTMLElement | null>(null);
  
  // Ref to prevent duplicate API calls
  const hasLoadedClassesRef = useRef(false);
  const lastClassIdRef = useRef<string | null>(null);
  
  // Constants
  const ITEMS_PER_PAGE = 10;
  const totalPages = Math.ceil(totalBeacons / ITEMS_PER_PAGE);

  // Fetch classes from real API
  const fetchClasses = async (): Promise<Class[]> => {
    try {
      setLoadingClasses(true);
      setError(null);
      const response = await apiService.get<{
        success: boolean;
        data: Class[];
        total: number;
        page: number;
        limit: number;
      }>('/classes');
      
      if (!response.success) {
        console.warn('Classes API returned unsuccessful response:', response);
        return [];
      }
      
      // Handle empty or missing data gracefully
      const classData = response.data || [];
      return classData;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch classes';
      console.error('Failed to load classes:', err);
      // Don't set error state for empty responses, just log it
      if (err instanceof Error && !err.message.includes('404')) {
        setError(errorMessage);
      }
      return [];
    } finally {
      setLoadingClasses(false);
    }
  };

  // Fetch subjects from real API
  const fetchSubjects = async (classId: string): Promise<Subject[]> => {
    try {
      setLoadingSubjects(true);
      setError(null);
      const response = await apiService.get<{
        success: boolean;
        data: Subject[];
        total: number;
        page: number;
        limit: number;
      }>(`/classes/${classId}/subjects`);
      
      if (!response.success) {
        console.warn('Subjects API returned unsuccessful response:', response);
        return [];
      }
      
      // Handle empty or missing data gracefully
      const subjectData = response.data || [];
      return subjectData;
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch subjects';
      console.error('Failed to load subjects:', err);
      
      // Don't set error state for authentication errors (they're handled by the API service)
      if (err.status >= 400 && err.status < 500) {
        console.warn('Authentication/client error fetching subjects:', errorMessage);
        return [];
      }
      
      // Don't set error state for empty responses or 404s, just log it
      if (err instanceof Error && !err.message.includes('404')) {
        setError(errorMessage);
      }
      return [];
    } finally {
      setLoadingSubjects(false);
    }
  };

  // Fetch chapters from real API
  const fetchChapters = async (subjectId: string): Promise<Chapter[]> => {
    try {
      setLoadingChapters(true);
      setError(null);
      const response = await apiService.get<{
        success: boolean;
        data: Chapter[];
        total: number;
        page: number;
        limit: number;
      }>(`/subjects/${subjectId}/chapters`);
      
      if (!response.success) {
        console.warn('Chapters API returned unsuccessful response:', response);
        return [];
      }
      
      // Handle empty or missing data gracefully
      const chapterData = response.data || [];
      return chapterData;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch chapters';
      console.error('Failed to load chapters:', err);
      // Don't set error state for empty responses or 404s, just log it
      if (err instanceof Error && !err.message.includes('404')) {
        setError(errorMessage);
      }
      return [];
    } finally {
      setLoadingChapters(false);
    }
  };

  // Fetch beacons (entities) from real API
  const fetchBeacons = async (chapterId: string, search: string = ''): Promise<{ beacons: Beacon[], total: number }> => {
    try {
      setError(null);
      
      // Find the subject_id for this chapter
      const currentSubject = subjects.find(s => 
        chapters.find(c => c.id === chapterId && c.subject_id === s.id)
      );
      
      if (!currentSubject) {
        console.warn('Subject not found for chapter:', chapterId);
        return { beacons: [], total: 0 };
      }

      const response = await apiService.get<{
        success: boolean;
        data: Array<{
          concepts?: Beacon[];
          topics?: Beacon[];
          events?: Beacon[];
          figures?: Beacon[];
          examples?: Beacon[];
          relationships?: Array<{
            source: string;
            target: string;
            type: string;
            strength?: number;
            description?: string;
          }>;
        }>;
        total: number;
        page: number;
        limit: number;
      }>(`/subjects/${currentSubject.id}/chapters/${chapterId}/entities?entities=concept,topic,figure,event,example&relationships=true`);
      
      if (!response.success) {
        console.warn('Beacons API returned unsuccessful response:', response);
        return { beacons: [], total: 0 };
      }

      // Flatten all entity types into a single array (excluding topics from display)
      const allBeacons: Beacon[] = [];
      if (response.data && response.data.length > 0) {
        const data = response.data[0];
        
        // Fetch all entity types but exclude topics from the display data
        const entityTypes = ['concepts', 'events', 'figures', 'examples'] as const;
        
        entityTypes.forEach(type => {
          if (data[type] && Array.isArray(data[type])) {
            allBeacons.push(...data[type]);
          }
        });

        // Process relationships data from API and distribute to beacons
        if (data.relationships && Array.isArray(data.relationships)) {
          // Create a map to group relationships by source beacon
          const relationshipsBySource = new Map<string, Array<{
            type: string;
            target_id: string;
            target_name: string;
            target_type: string;
            strength?: number;
            description?: string;
          }>>();

          data.relationships.forEach((rel: any) => {
            if (rel.source && rel.target) {
              const sourceId = rel.source;
              const targetId = rel.target;
              
              // Find source and target beacons in our allBeacons array
              const sourceBeacon = allBeacons.find(b => b.id === sourceId);
              const targetBeacon = allBeacons.find(b => b.id === targetId);
              
              // Add forward relationship (source -> target)
              if (targetBeacon) {
                if (!relationshipsBySource.has(sourceId)) {
                  relationshipsBySource.set(sourceId, []);
                }
                
                relationshipsBySource.get(sourceId)?.push({
                  type: rel.type || 'related_to',
                  target_id: targetId,
                  target_name: targetBeacon.name,
                  target_type: targetBeacon.type,
                  strength: rel.strength,
                  description: rel.description
                });
              }

              // Add reverse relationship (target <- source)
              if (sourceBeacon) {
                if (!relationshipsBySource.has(targetId)) {
                  relationshipsBySource.set(targetId, []);
                }
                
                relationshipsBySource.get(targetId)?.push({
                  type: `reverse_${rel.type || 'related_to'}`,
                  target_id: sourceId,
                  target_name: sourceBeacon.name,
                  target_type: sourceBeacon.type,
                  strength: rel.strength,
                  description: rel.description ? `Reverse: ${rel.description}` : undefined
                });
              }
            }
          });

          // Distribute relationships to beacons
          allBeacons.forEach(beacon => {
            const beaconRelationships = relationshipsBySource.get(beacon.id) || [];
            beacon.relationships = beaconRelationships;
          });
        }
      }

      // Apply search filter
      let filteredBeacons = allBeacons;
      if (search) {
        filteredBeacons = allBeacons.filter(beacon =>
          beacon.name?.toLowerCase().includes(search.toLowerCase()) ||
          (beacon.description?.toLowerCase().includes(search.toLowerCase()) ?? false) ||
          (beacon.definition?.toLowerCase().includes(search.toLowerCase()) ?? false) ||
          (beacon.keywords?.some(keyword => keyword.toLowerCase().includes(search.toLowerCase())) ?? false)
        );
      }

      return { beacons: filteredBeacons, total: filteredBeacons.length };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch beacons';
      console.error('Failed to load beacons:', err);
      // Don't set error state for empty responses or 404s, just log it
      if (err instanceof Error && !err.message.includes('404')) {
        setError(errorMessage);
      }
      return { beacons: [], total: 0 };
    }
  };

  // Load classes on component mount (only for non-students)
  useEffect(() => {
    // Skip loading classes for students since they don't need class selection
    if (isStudent || hasLoadedClassesRef.current) return;
    
    const loadClasses = async (retryCount = 0) => {
      const maxRetries = 3;
      hasLoadedClassesRef.current = true; // Set this early to prevent duplicate calls
      
      try {
        const classData = await fetchClasses();
        setClasses(classData);
      } catch (error) {
        console.error('Error loading classes:', error);
        // Only retry on actual errors, not on empty responses (which are handled in fetchClasses)
        if (retryCount < maxRetries) {
          hasLoadedClassesRef.current = false; // Reset ref to allow retry
          setTimeout(() => loadClasses(retryCount + 1), 1000 * (retryCount + 1)); // Exponential backoff
          return;
        } else {
          console.error(`Failed to load classes after ${maxRetries} retries`);
        }
      }
    };
    
    loadClasses();
  }, [apiService, isStudent]); // Include isStudent to prevent loading for students

  // Auto-select class for students based on their profile (for UI display only)
  useEffect(() => {
    if (isStudent && profile?.student_details?.class_id && !selectedClass) {
      // Set selected class for UI display (though the API will use class_id directly)
      setSelectedClass(profile.student_details.class_id);
    }
  }, [isStudent, profile?.student_details?.class_id, selectedClass]);

  // Get class ID for students from profile, for other users use selected class
  const getClassIdForApi = (): string => {
    if (isStudent && profile?.student_details?.class_id) {
      // Use class ID directly from student's profile
      return profile.student_details.class_id;
    }
    
    return selectedClass;
  };

  // Load subjects when class changes or when student profile is available
  useEffect(() => {
    const classIdToUse = getClassIdForApi();
    
    // Prevent duplicate API calls for the same class ID
    if (classIdToUse && classIdToUse !== lastClassIdRef.current) {
      lastClassIdRef.current = classIdToUse;
      
      const loadSubjects = async () => {
        // Reset dependent state
        setSubjects([]);
        setChapters([]);
        setAllBeacons([]);
        setBeacons([]);
        setSelectedSubject('');
        setSelectedChapter('');
        setTotalBeacons(0);
        setCurrentPage(1);
        
        const subjectData = await fetchSubjects(classIdToUse);
        setSubjects(subjectData);
      };
      loadSubjects();
    } else if (!classIdToUse && lastClassIdRef.current) {
      // Clear all data when no class is selected
      lastClassIdRef.current = null;
      setSubjects([]);
      setChapters([]);
      setAllBeacons([]);
      setBeacons([]);
      setTotalBeacons(0);
    }
  }, [selectedClass, isStudent, profile?.student_details?.class_id]); // Optimized dependencies

  // Load chapters when subject changes
  useEffect(() => {
    if (selectedSubject) {
      const loadChapters = async () => {
        // Reset dependent state
        setChapters([]);
        setAllBeacons([]);
        setBeacons([]);
        setSelectedChapter('');
        setTotalBeacons(0);
        setCurrentPage(1);
        
        const chapterData = await fetchChapters(selectedSubject);
        setChapters(chapterData);
      };
      loadChapters();
    } else {
      // Clear all data when no subject is selected
      setChapters([]);
      setAllBeacons([]);
      setBeacons([]);
      setTotalBeacons(0);
    }
  }, [selectedSubject]);

  // Load beacons when chapter changes
  useEffect(() => {
    if (selectedChapter) {
      const loadBeacons = async () => {
        setLoading(true);
        setAllBeacons([]);
        setBeacons([]);
        setTotalBeacons(0);
        setCurrentPage(1);
        
        const { beacons: beaconData, total } = await fetchBeacons(selectedChapter, searchTerm);
        setAllBeacons(beaconData);
        setTotalBeacons(total);
        setLoading(false);
      };
      loadBeacons();
    } else {
      // Clear beacon data when no chapter is selected
      setAllBeacons([]);
      setBeacons([]);
      setTotalBeacons(0);
      setLoading(false);
    }
  }, [selectedChapter, searchTerm]);

  // Apply pagination and sorting when allBeacons, currentPage, sortColumn, or sortDirection change
  useEffect(() => {
    let sortedBeacons = [...allBeacons];
    
    // Always apply sorting (since we have a default sort column)
    sortedBeacons.sort((a, b) => {
      let comparison: number;
      
      switch (sortColumn) {
        case 'name':
          const aName = a.name || '';
          const bName = b.name || '';
          comparison = aName.localeCompare(bName);
          break;
        case 'type':
          const aType = a.type || '';
          const bType = b.type || '';
          comparison = aType.localeCompare(bType);
          break;
        case 'description':
          const aDesc = a.description || a.definition || '';
          const bDesc = b.description || b.definition || '';
          comparison = aDesc.localeCompare(bDesc);
          break;
        case 'relationships':
          const aCount = a.relationships?.length || 0;
          const bCount = b.relationships?.length || 0;
          comparison = aCount - bCount;
          break;
        default:
          comparison = 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    // Apply pagination
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedBeacons = sortedBeacons.slice(startIndex, endIndex);
    setBeacons(paginatedBeacons);
  }, [allBeacons, currentPage, sortColumn, sortDirection]);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleSort = (column: 'name' | 'type' | 'description' | 'relationships') => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending (except for relationships which defaults to descending)
      setSortColumn(column);
      setSortDirection(column === 'relationships' ? 'desc' : 'asc');
    }
    // Reset to first page when sorting changes
    setCurrentPage(1);
  };

  const getSortIcon = (column: 'name' | 'type' | 'description' | 'relationships') => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-4 w-4" />;
    }
    return sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  // Handler for showing relationships
  const handleShowRelationships = (beacon: Beacon) => {
    // Capture the currently focused element before opening modal
    modalTriggerRef.current = document.activeElement as HTMLElement;
    
    // Close the dropdown immediately
    setOpenDropdownId(null);
    
    // Small delay to allow dropdown to close before opening modal
    setTimeout(() => {
      // Blur any focused elements to prevent focus conflicts with aria-hidden
      if (document.activeElement && 'blur' in document.activeElement) {
        (document.activeElement as HTMLElement).blur();
      }
      
      setSelectedBeaconForRelationships(beacon);
      setRelationshipModalOpen(true);
    }, 100);
  };

  // Handler for closing relationship modal
  const handleCloseRelationshipModal = () => {
    setRelationshipModalOpen(false);
    setSelectedBeaconForRelationships(null);
  };

  const handleReset = () => {
    setSelectedClass('');
    setSelectedSubject('');
    setSelectedChapter('');
    setSubjects([]);
    setChapters([]);
    setAllBeacons([]);
    setBeacons([]);
    setSearchTerm('');
    setCurrentPage(1);
    setTotalBeacons(0);
    setSortColumn('relationships');
    setSortDirection('desc');
    setLoadingSubjects(false);
    setLoadingChapters(false);
    setLoading(false);
    setError(null);
  };


  // Handler for Learn action - navigate to chat page in same tab
  const handleLearnBeacon = (beacon: Beacon) => {
    // Close the dropdown
    setOpenDropdownId(null);
    
    // Simple same-tab navigation without state - always fetch from API
    navigate(`/beacons/learn/${beacon.id}`);
  };

  const handleCloseRelationships = () => {
    setSelectedBeaconForRelationships(null);
    setRelationshipModalOpen(false);
    
    // Restore focus to the trigger element after modal closes
    // Use a longer timeout to ensure the modal and any aria-hidden attributes have been removed
    setTimeout(() => {
      if (modalTriggerRef.current) {
        try {
          // Make sure the element is still in the DOM and focusable
          if (document.contains(modalTriggerRef.current) && modalTriggerRef.current.focus) {
            modalTriggerRef.current.focus();
          }
        } catch (error) {
          console.warn('Failed to restore focus:', error);
        } finally {
          modalTriggerRef.current = null; // Clear the reference
        }
      }
    }, 200);
  };

  if (!user) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Knowledge Beacons</h1>
          <p className="text-muted-foreground">
            Browse and search through Knowledge Beacons organized by class, subject, and chapter.
          </p>
        </div>
        <Button onClick={handleReset} variant="outline" size="sm">
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset Filters
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-800">
              <span className="font-medium">Error:</span>
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className={`grid grid-cols-1 gap-4 ${isStudent ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
            {/* Class Selection - Hidden for students since they should only see their own class content */}
            {!isStudent && (
              <div className="space-y-2">
                <Label htmlFor="class-select">Class</Label>
                <Select value={selectedClass} onValueChange={setSelectedClass} disabled={loadingClasses}>
                  <SelectTrigger id="class-select">
                    <SelectValue placeholder={loadingClasses ? "Loading classes..." : "Select a class"} />
                  </SelectTrigger>
                  <SelectContent>
                    {classes.map((classItem) => (
                      <SelectItem key={classItem.id} value={classItem.id}>
                        {classItem.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Subject Selection */}
            <div className="space-y-2">
              <Label htmlFor="subject-select">Subject</Label>
              <Select 
                value={selectedSubject} 
                onValueChange={setSelectedSubject}
                disabled={!getClassIdForApi() || loadingSubjects}
              >
                <SelectTrigger id="subject-select">
                  <SelectValue placeholder={
                    !getClassIdForApi() 
                      ? (isStudent ? "Loading your subjects..." : "Select a class first")
                      : loadingSubjects 
                        ? "Loading subjects..." 
                        : subjects.length === 0 && getClassIdForApi() 
                          ? "No subjects available"
                          : "Select a subject"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {subjects.length > 0 ? (
                    subjects.map((subject) => (
                      <SelectItem key={subject.id} value={subject.id}>
                        {formatSubjectName(subject.display_name || subject.name)}
                      </SelectItem>
                    ))
                  ) : (
                    getClassIdForApi() && !loadingSubjects && (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">
                        No subjects available
                      </div>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Chapter Selection */}
            <div className="space-y-2">
              <Label htmlFor="chapter-select">Chapter</Label>
              <Select 
                value={selectedChapter} 
                onValueChange={setSelectedChapter}
                disabled={!selectedSubject || loadingChapters}
              >
                <SelectTrigger id="chapter-select">
                  <SelectValue placeholder={
                    !selectedSubject 
                      ? "Select a subject first" 
                      : loadingChapters 
                        ? "Loading chapters..." 
                        : chapters.length === 0 && selectedSubject 
                          ? "No chapters available"
                          : "Select a chapter"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {chapters.length > 0 ? (
                    chapters.map((chapter) => (
                      <SelectItem key={chapter.id} value={chapter.id}>
                        {formatSubjectName(chapter.title)}
                      </SelectItem>
                    ))
                  ) : (
                    selectedSubject && !loadingChapters && (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">
                        No chapters available
                      </div>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Search Bar */}
          {selectedChapter && (
            <div className="space-y-2">
              <Label htmlFor="search">Search Beacons</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Search by name, description, definition, or keywords..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results Section */}
      {selectedChapter && (
        <Card>
          <CardHeader>
            <CardTitle>Beacons</CardTitle>
            <p className="text-sm text-muted-foreground">
              {totalBeacons} beacon{totalBeacons !== 1 ? 's' : ''} found
            </p>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-2 text-sm text-muted-foreground">Loading beacons...</p>
                </div>
              </div>
            ) : beacons.length > 0 ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        <Button
                          variant="ghost"
                          onClick={() => handleSort('name')}
                          className="h-auto p-0 font-medium hover:bg-transparent"
                        >
                          Name
                          <span className="ml-2">
                            {getSortIcon('name')}
                          </span>
                        </Button>
                      </TableHead>
                      <TableHead>
                        <Button
                          variant="ghost"
                          onClick={() => handleSort('type')}
                          className="h-auto p-0 font-medium hover:bg-transparent"
                        >
                          Type
                          <span className="ml-2">
                            {getSortIcon('type')}
                          </span>
                        </Button>
                      </TableHead>
                      <TableHead>
                        <Button
                          variant="ghost"
                          onClick={() => handleSort('description')}
                          className="h-auto p-0 font-medium hover:bg-transparent"
                        >
                          Description
                          <span className="ml-2">
                            {getSortIcon('description')}
                          </span>
                        </Button>
                      </TableHead>
                      <TableHead>Keywords</TableHead>
                      <TableHead>
                        <Button
                          variant="ghost"
                          onClick={() => handleSort('relationships')}
                          className="h-auto p-0 font-medium hover:bg-transparent"
                        >
                          Relationships
                          <span className="ml-2">
                            {getSortIcon('relationships')}
                          </span>
                        </Button>
                      </TableHead>
                      <TableHead className="w-[70px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {beacons.map((beacon) => (
                      <TableRow key={beacon.id}>
                        <TableCell className="font-medium">{formatSubjectName(beacon.name)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {beacon.type}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-md">
                          <p className="truncate" title={beacon.description || beacon.definition}>
                            {beacon.description || beacon.definition || 'No description'}
                          </p>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {beacon.keywords && beacon.keywords.length > 0 ? (
                              <>
                                {beacon.keywords.slice(0, 3).map((keyword) => (
                                  <Badge key={keyword} variant="outline" className="text-xs">
                                    {keyword}
                                  </Badge>
                                ))}
                                {beacon.keywords.length > 3 && (
                                  <Badge 
                                    variant="outline" 
                                    className="text-xs cursor-pointer hover:bg-muted"
                                    title={`All keywords: ${beacon.keywords.join(', ')}`}
                                  >
                                    +{beacon.keywords.length - 3} more
                                  </Badge>
                                )}
                              </>
                            ) : (
                              <span className="text-muted-foreground text-sm">No keywords</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge variant="secondary" className="text-xs">
                              {beacon.relationships?.length || 0}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu 
                            open={openDropdownId === beacon.id} 
                            onOpenChange={(open) => setOpenDropdownId(open ? beacon.id : null)}
                          >
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <span className="sr-only">Open menu</span>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuItem onClick={() => handleLearnBeacon(beacon)}>Learn</DropdownMenuItem>
                              {beacon.relationships && beacon.relationships.length > 0 && (
                                <DropdownMenuItem onClick={() => handleShowRelationships(beacon)}>Show Relationships</DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between space-x-2 py-4">
                    <div className="text-sm text-muted-foreground">
                      Page {currentPage} of {totalPages}
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4 mr-2" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">
                  {searchTerm ? 'No beacons match your search criteria.' : 'No beacons found for this chapter.'}
                </p>
                {searchTerm && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-2"
                    onClick={() => setSearchTerm('')}
                  >
                    Clear search
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!selectedChapter && (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground">
              {isStudent 
                ? "Select a subject and chapter to view Knowledge Beacons."
                : "Select a class, subject, and chapter to view Knowledge Beacons."
              }
            </p>
          </CardContent>
        </Card>
      )}

      {/* Relationship Modal */}
      <RelationshipModal 
        isOpen={relationshipModalOpen}
        beacon={selectedBeaconForRelationships}
        onClose={handleCloseRelationships}
        onFetchRelationships={async (beaconId: string) => {
          // Find the beacon with relationships from our existing data
          const selectedBeacon = allBeacons.find(b => b.id === beaconId);
          
          if (selectedBeacon) {
            return selectedBeacon;
          }
          
          // Fallback if beacon not found in current data
          return selectedBeaconForRelationships || { 
            id: beaconId, 
            type: 'unknown', 
            name: 'Unknown Beacon',
            subject_id: '',
            chapter_id: '',
            relationships: []
          };
        }}
      />
    </div>
  );
}
